import { HealthScoreType } from '@constants/health-score-info';

import { VitalSignObservationRecord } from '@models/vital-sign';

import { formatPatientId } from '@utils/format';

import type { ListLatestHealthScoreObservationsQueryVariables } from 'generated/health-score.generated';
import {
  useListHealthScoreObservations,
  useListLatestHealthScoreObservations,
} from 'generated/health-score.generated';

import { useResponseHandler } from '../useResponseHandler';
import { useSession } from '../useSession';

// Could be used later for health score drilldown or detailed analytics
export const useGetHealthScoreObservations = (
  code?: string,
  skipQuery?: boolean,
  dateRange?: DateRange
) => {
  const {
    state: { activeProfile },
  } = useSession();
  const { handleError } = useResponseHandler();
  const patient_id = formatPatientId(activeProfile?.patient_id) ?? '';

  const response = useListHealthScoreObservations({
    variables: {
      input: {
        patient: patient_id,
        code,
        dateFrom: dateRange?.dateFrom,
        dateTo: dateRange?.dateTo,
      },
    },
    skip: !patient_id || Boolean(skipQuery),
  });
  const { data, error } = response;
  handleError(error);
  const observations = (data?.listHealthScoreObservations.data ?? []).map(
    (o) => new VitalSignObservationRecord(o)
  );
  return { ...response, data: observations };
};

export const useLatestHealthScores = (
  params?: Omit<ListLatestHealthScoreObservationsQueryVariables, 'input'>
) => {
  const {
    state: { activeProfile },
  } = useSession();
  const { handleError } = useResponseHandler();

  const patient_id = activeProfile?.patient_id ?? '';
  const response = useListLatestHealthScoreObservations({
    variables: { input: { patient: patient_id }, ...params },
    fetchPolicy: 'cache-and-network',
    skip: !patient_id,
  });
  const { data, error } = response;
  handleError(error);
  const observations = data?.listLatestHealthScoreObservations;

  return {
    ...response,
    data: {
      ...(params?.cardiovascular_score
        ? {
            [HealthScoreType.Cardiovascular]: observations?.cardiovascular_score
              ? new VitalSignObservationRecord(
                  observations.cardiovascular_score
                )
              : null,
          }
        : {}),
      ...(params?.focus_score
        ? {
            [HealthScoreType.Focus]: observations?.focus_score
              ? new VitalSignObservationRecord(observations.focus_score)
              : null,
          }
        : {}),
      ...(params?.respiratory_score
        ? {
            [HealthScoreType.Respiratory]: observations?.respiratory_score
              ? new VitalSignObservationRecord(observations.respiratory_score)
              : null,
          }
        : {}),
      ...(params?.immunity_score
        ? {
            [HealthScoreType.Immune]: observations?.immunity_score
              ? new VitalSignObservationRecord(observations.immunity_score)
              : null,
          }
        : {}),
      ...(params?.sleep_quality_score
        ? {
            [HealthScoreType.Sleep]: observations?.sleep_quality_score
              ? new VitalSignObservationRecord(observations.sleep_quality_score)
              : null,
          }
        : {}),
      ...(params?.stress_score
        ? {
            [HealthScoreType.Stress]: observations?.stress_score
              ? new VitalSignObservationRecord(observations.stress_score)
              : null,
          }
        : {}),
    },
  };
};
